import { INSERT_ORDERED_LIST_COMMAND, INSERT_UNORDERED_LIST_COMMAND, REMOVE_LIST_COMMAND, } from "@lexical/list";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { mergeRegister } from "@lexical/utils";
import cn from "clsx";
import { $getSelection, $isRangeSelection, COMMAND_PRIORITY_LOW, FORMAT_TEXT_COMMAND, SELECTION_CHANGE_COMMAND, } from "lexical";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { FaIcon, FaIcons } from "../fa-icon/index.js";
const ToolbarItem = ({ active, onMouseDown, icon, ariaLabel, readonly, }) => {
    return (React.createElement("button", { onMouseDown: onMouseDown, className: cn("in-rich-text-editor-toolbar-item", active && "in-rich-text-editor-toolbar-item-active", readonly && "in-rich-text-editor-toolbar-item-readonly"), "aria-label": ariaLabel, type: "button", disabled: readonly },
        React.createElement(FaIcon, { name: icon, width: 16, height: 16 })));
};
const ToolbarPlugin = ({ readonly }) => {
    const [editor] = useLexicalComposerContext();
    const toolbarRef = useRef(null);
    const [isBold, setIsBold] = useState(false);
    const [isItalic, setIsItalic] = useState(false);
    const [isUnderline, setIsUnderline] = useState(false);
    const [isOrderedList, setIsOrderedList] = useState(false);
    const [isUnorderedList, setIsUnorderedList] = useState(false);
    const $updateToolbar = useCallback(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
            // Update text format
            setIsBold(selection.hasFormat("bold"));
            setIsItalic(selection.hasFormat("italic"));
            setIsUnderline(selection.hasFormat("underline"));
            // Get the parent node of the selection to check for list state
            const anchorNode = selection.anchor.getNode();
            const element = anchorNode.getKey() === "root"
                ? anchorNode
                : anchorNode.getTopLevelElementOrThrow();
            const elementKey = element.getKey();
            const elementDOM = editor.getElementByKey(elementKey);
            // Check if selection is in a list item
            if (elementDOM) {
                const parentList = elementDOM.closest("ul, ol");
                if (parentList) {
                    setIsOrderedList(parentList.tagName === "OL");
                    setIsUnorderedList(parentList.tagName === "UL");
                }
                else {
                    setIsOrderedList(false);
                    setIsUnorderedList(false);
                }
            }
        }
    }, [editor]);
    useEffect(() => {
        return mergeRegister(editor.registerUpdateListener(({ editorState }) => {
            editorState.read(() => {
                $updateToolbar();
            });
        }), editor.registerCommand(SELECTION_CHANGE_COMMAND, (_payload, _newEditor) => {
            $updateToolbar();
            return false;
        }, COMMAND_PRIORITY_LOW));
    }, [editor, $updateToolbar]);
    // Handler for button click that prevents losing editor focus
    const formatTextAndPreventDefault = (event, format) => {
        event.preventDefault();
        editor.dispatchCommand(FORMAT_TEXT_COMMAND, format);
        // Return focus to the editor
        editor.focus();
    };
    return (React.createElement("div", { className: cn("in-rich-text-editor-toolbar", {
            "in-rich-text-editor-toolbar-readonly": readonly,
        }), ref: toolbarRef },
        React.createElement(ToolbarItem, { active: isBold, onMouseDown: (e) => formatTextAndPreventDefault(e, "bold"), icon: FaIcons.BoldRegular, ariaLabel: "Fet tekst", readonly: readonly }),
        React.createElement(ToolbarItem, { active: isItalic, onMouseDown: (e) => formatTextAndPreventDefault(e, "italic"), icon: FaIcons.ItalicRegular, ariaLabel: "Kursiv", readonly: readonly }),
        React.createElement(ToolbarItem, { active: isUnderline, onMouseDown: (e) => formatTextAndPreventDefault(e, "underline"), icon: FaIcons.UnderlineRegular, ariaLabel: "Understreking", readonly: readonly }),
        React.createElement("span", { className: cn("in-rich-text-editor-divider") }),
        React.createElement(ToolbarItem, { active: isUnorderedList, onMouseDown: (e) => {
                e.preventDefault();
                editor.dispatchCommand(isUnorderedList
                    ? REMOVE_LIST_COMMAND
                    : INSERT_UNORDERED_LIST_COMMAND, undefined);
                editor.focus();
            }, icon: FaIcons.ListUlRegular, ariaLabel: "Unumerert liste", readonly: readonly }),
        React.createElement(ToolbarItem, { active: isOrderedList, onMouseDown: (e) => {
                e.preventDefault();
                editor.dispatchCommand(isOrderedList ? REMOVE_LIST_COMMAND : INSERT_ORDERED_LIST_COMMAND, undefined);
                editor.focus();
            }, icon: FaIcons.ListOlRegular, ariaLabel: "Numerert liste", readonly: readonly })));
};
export default ToolbarPlugin;
//# sourceMappingURL=toolbar-plugin.js.map